/**
 * Tenant read-cache clear API wrapper (v2 — Project Independence, DNKADM-0062;
 * re-points the v1 `/admin/caches/clear` to `/v2/admin/caches/clear`, handoff
 * §5.21 [≈] — unchanged behavior). Backs the Settings "Cached data" → "Refresh
 * cached data" action (DNKADM-0045): an OWNER/ADMIN forces the next read to
 * re-fetch fresh data (staff, locations, payment-connection state, custom-field
 * registry, …) instead of waiting out the server-side cache TTL.
 *
 * Contract (handoff §5.21, ASSUMED — dink-api's parallel track; MSW-tested here;
 * may not be live in a local backend):
 *  - `POST /v2/admin/caches/clear` — OWNER/ADMIN, tenant resolved from the JWT, **no
 *    body**. Bearer-only `/admin/**` surface via `request()` (NO CSRF, NO
 *    cookies — the bounded 401→refresh→retry applies).
 *  - `200 { tenantSlug: string, cleared: string[], count: number }` — the
 *    internal cache labels cleared for THIS tenant plus a count. Best-effort /
 *    non-destructive: a partial clear still returns `200` with the report, so the
 *    UI treats ANY `200` as success — the button never errors the screen over a
 *    partial failure.
 *  - `403` for a non-OWNER/ADMIN. No audit entry is written (it changes no stored
 *    data).
 *
 * Bearer-only `/v2/admin/**` surface via `request()` — the bearer token, NO CSRF,
 * NO cookies, the bounded 401→refresh→retry, and the mutating-audit `X-Session-Mode`
 * stamp (DNKADM-0049). The `200` parse is DEFENSIVE (mirrors the other `src/api/*.ts` wrappers): a
 * missing or wrong-typed field DEGRADES rather than throwing — a non-array
 * `cleared` → `[]`, a missing/wrong-typed `count` → `cleared.length`, a
 * missing/wrong-typed `tenantSlug` → `null`. It never throws on a `200` shape
 * problem; only a non-2xx throws the typed `ApiError` (the UI maps `403`).
 */
import { request } from './client';

/**
 * Parsed `POST /admin/caches/clear` report. `cleared` is the internal cache
 * labels reset for the current tenant; `count` is how many (degraded to
 * `cleared.length` when the server omits it); `tenantSlug` is the tenant whose
 * caches were cleared (degraded to `null` when absent).
 */
export interface ClearCachesResult {
  tenantSlug: string | null;
  cleared: string[];
  count: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Parse the `200` body defensively. Never throws on a shape problem: a non-array
 * `cleared` degrades to `[]`; a missing/wrong-typed `count` degrades to
 * `cleared.length`; a missing/wrong-typed `tenantSlug` degrades to `null`.
 */
function parseResult(raw: unknown): ClearCachesResult {
  const o = isObject(raw) ? raw : {};
  const cleared = Array.isArray(o['cleared'])
    ? o['cleared'].filter((label): label is string => typeof label === 'string')
    : [];
  const rawCount = o['count'];
  const count =
    typeof rawCount === 'number' && Number.isFinite(rawCount) ? rawCount : cleared.length;
  return { tenantSlug: asText(o['tenantSlug']), cleared, count };
}

/**
 * `POST /v2/admin/caches/clear` (no body) — clear the current tenant's server-side
 * read-caches. Bearer-only (no CSRF, no cookies). Resolves to a defensively
 * parsed {@link ClearCachesResult} on ANY `200` (including a best-effort partial
 * clear). Throws a typed `ApiError` on a non-2xx response (`403` for a
 * non-OWNER/ADMIN, mapped to an error toast by the caller).
 */
export async function clearCaches(): Promise<ClearCachesResult> {
  const body = await request<unknown>('POST', '/v2/admin/caches/clear');
  return parseResult(body);
}
